import { useMemo } from "react";
import {
  useScopedLocationIds,
  ScopedLocationIds,
} from "hooks/useAdminGroup/useScopedLocationIds";
import { useHierarchyExternalIds } from "hooks/useAdminGroup/useHierarchyExternalIds";
import {
  deriveScopedApiFilters,
  DeriveScopedApiFiltersResult,
} from "hooks/useAdminGroup/deriveScopedApiFilters";
import type { ChartFilters } from "hooks/useAdminGroup/applyLocationScope";

export type UseScopedApiFiltersResult = {
  /** The raw scope, for rendering through LocationScopeGate. */
  scope: ScopedLocationIds;
} & DeriveScopedApiFiltersResult;

// Bundles the scope + hierarchy hooks with the deriveScopedApiFilters
// decision table for widgets that fetch outside ReportPageChart (the Quality
// tab's panels/tables/header strip). Returns the raw scope too, so callers
// can render through LocationScopeGate.
//
// The derived result is memoized on the values the decision table actually
// reads -- the scope object itself is a fresh literal every render, but
// scopeExternalIds and the hierarchy result are memoized upstream -- so
// consumers can hang fetch effects and componentDidUpdate comparisons off
// the result without refetch churn.
export const useScopedApiFilters = (
  apiFilters: ChartFilters,
  filterOptions?: unknown,
): UseScopedApiFiltersResult => {
  const scope = useScopedLocationIds();
  const hierarchy = useHierarchyExternalIds();

  const { isLbaEnabled, applyScope, scopeExternalIds } = scope;

  const derived = useMemo<DeriveScopedApiFiltersResult>(
    () =>
      deriveScopedApiFilters({
        apiFilters,
        filterOptions,
        scope: { isLbaEnabled, applyScope, scopeExternalIds },
        hierarchy,
      }),
    [apiFilters, filterOptions, isLbaEnabled, applyScope, scopeExternalIds, hierarchy],
  );

  return { scope, ...derived };
};
